import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip } from 'recharts';
import { extractApiMessage } from '@/lib/api';
import { formatoDecimal, formatoEuros } from '@/lib/liquidacion';
import {
  fechaEvolucion,
  restarDecimal,
  type EvolucionLiquidacion,
} from '@/lib/liquidacion-evolucion';
import { useLiquidacionEvolucion } from '@/lib/queries/liquidacion';
import { useAuthStore } from '@/stores/auth';
import { useEntityId } from '@/lib/offline/operations';
import { LiquidacionImportes } from '@/components/LiquidacionDesglose';
import { EvolucionAvisos } from '@/components/EvolucionAvisos';

export default function LiquidacionEvolucionPage() {
  const { t } = useTranslation();
  const { camadaId } = useParams();
  const user = useAuthStore((s) => s.user);
  const permitted = user?.rol !== 'operario';
  const id = useEntityId(camadaId, 'camadas');
  const q = useLiquidacionEvolucion(permitted ? id : null);
  return (
    <main className="mx-auto max-w-3xl px-4 pb-8">
      <Link
        to="/liquidacion"
        className="inline-flex min-h-11 items-center text-sm text-accent-icon"
      >
        {t('liquidacion.volver_listado')}
      </Link>
      <h1 className="text-xl font-bold">{t('evolucion.titulo')}</h1>
      {!permitted ? (
        <p role="alert" className="mt-4 text-warning-text">
          {t('liquidacion.sin_permiso')}
        </p>
      ) : id === null ? (
        <p role="alert">{t('evolucion.id_invalido')}</p>
      ) : q.isError ? (
        <div role="alert" className="mt-4 text-warning-text">
          <p>{extractApiMessage(q.error)}</p>
          <button className="min-h-11 text-accent-icon" onClick={() => void q.refetch()}>
            {t('evolucion.reintentar')}
          </button>
        </div>
      ) : q.isFetching || !q.data ? (
        <p role="status" className="mt-4">
          {t('liquidacion.calculando')}
        </p>
      ) : (
        <Evolucion data={q.data} key={id} />
      )}
    </main>
  );
}

function Evolucion({ data }: { data: EvolucionLiquidacion }) {
  const { t } = useTranslation();
  const [hastaElegida, setHasta] = useState('');
  const [desdeElegida, setDesde] = useState('');
  const ultima = data.serie.at(-1);
  const final = data.serie.find((d) => d.fecha === hastaElegida) ?? ultima;
  const index = data.serie.findIndex((d) => d.fecha === final?.fecha);
  const inicial =
    data.serie.find((d) => d.fecha === desdeElegida) ?? data.serie[Math.max(0, index - 7)];
  if (!final || !inicial) return <p className="mt-4">{t('evolucion.sin_serie')}</p>;
  const grafica = data.serie.map((d) => ({
    fecha: d.fecha,
    importe: d.resultado ? Number(d.resultado.total_eur) : null,
    exacto: d.resultado?.total_eur,
  }));
  const comparable = final.comparable && inicial.comparable;
  const ventana = data.peor_ventana;
  const campos = ['total_eur', 'bruto_eur', 'iva_eur', 'importe_bajas_eur'] as const;
  return (
    <>
      <p className="mt-1 font-semibold">{data.codigo_lote}</p>
      <p className="mt-1 text-sm text-text-secondary">
        {t('evolucion.recalculado', { fecha: fechaEvolucion(data.fecha_calculo) })}
      </p>
      <p className="mt-4 text-sm text-text-secondary">{t('evolucion.metodo')}</p>
      <section className="mt-6 border-t border-border pt-4" aria-label={t('evolucion.escenario')}>
        <h2 className="mb-3 text-base font-semibold">
          {t('evolucion.escenario_fecha', { fecha: fechaEvolucion(final.fecha) })}
        </h2>
        {final.resultado ? (
          <LiquidacionImportes resultado={final.resultado} />
        ) : (
          <p className="font-medium">{t('evolucion.pendiente')}</p>
        )}
        <div className="mt-3">
          <EvolucionAvisos dia={final} />
        </div>
        <dl className="mt-4 grid grid-cols-2 gap-3 text-sm tabular-nums">
          <div>
            <dt className="text-text-secondary">{t('evolucion.aves_restantes')}</dt>
            <dd>{formatoDecimal(final.aves_restantes, 0)}</dd>
          </div>
          <div>
            <dt className="text-text-secondary">{t('evolucion.pienso_acumulado')}</dt>
            <dd>{final.kg_pienso === null ? '—' : `${formatoDecimal(final.kg_pienso)} kg`}</dd>
          </div>
        </dl>
        {final.fecha_ultima_entrega && (
          <p className="mt-3 text-sm text-text-secondary">
            {t('evolucion.ultima_entrega', {
              fecha: fechaEvolucion(final.fecha_ultima_entrega),
              kg: formatoDecimal(final.kg_ultima_entrega),
            })}
          </p>
        )}
      </section>
      <section className="mt-7 border-t border-border pt-4" aria-label={t('evolucion.comparacion')}>
        <h2 className="mb-3 text-base font-semibold">{t('evolucion.comparacion')}</h2>
        <div className="grid grid-cols-2 gap-3">
          {(
            [
              [t('evolucion.desde'), inicial.fecha, setDesde],
              [t('evolucion.hasta'), final.fecha, setHasta],
            ] as const
          ).map(([label, value, set]) => (
            <label className="min-w-0 text-sm font-medium" key={label}>
              {label}
              <input
                className="mt-1 min-h-11 w-full min-w-0 rounded-lg border border-border bg-surface px-2"
                type="date"
                min={data.fecha_desde}
                max={data.fecha_hasta}
                value={value}
                onChange={(e) => {
                  if (data.serie.some((d) => d.fecha === e.target.value)) set(e.target.value);
                }}
              />
            </label>
          ))}
        </div>
        <div className="mt-2 flex gap-3">
          {[7, 14].map((n) => (
            <button
              key={n}
              disabled={index < n}
              onClick={() => setDesde(data.serie[index - n].fecha)}
              className="min-h-11 px-2 text-sm text-accent-icon disabled:opacity-40"
            >
              {t('evolucion.dias_antes', { count: n })}
            </button>
          ))}
        </div>
        {final.fecha <= inicial.fecha ? (
          <p role="alert" className="mt-3 text-warning-text">
            {t('evolucion.orden_fechas')}
          </p>
        ) : (
          <>
            <p className="mt-2 text-sm text-text-secondary">
              {t(
                comparable ? 'evolucion.misma_configuracion' : 'evolucion.comparacion_provisional',
              )}
            </p>
            {final.resultado && inicial.resultado ? (
              <dl className="mt-3 space-y-2 text-sm tabular-nums">
                {campos.map((c) => (
                  <div key={c} className="flex justify-between gap-3">
                    <dt>{t(`liquidacion.${c === 'total_eur' ? 'total' : c}`)}</dt>
                    <dd className="break-all text-right font-semibold">
                      {formatoEuros(restarDecimal(final.resultado![c], inicial.resultado![c]))}
                    </dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="mt-3">{t('evolucion.comparacion_pendiente')}</p>
            )}
            <dl className="mt-4 grid grid-cols-2 gap-3 text-sm tabular-nums">
              <div>
                <dt className="text-text-secondary">{t('evolucion.bajas_periodo')}</dt>
                <dd>{final.bajas_registradas - inicial.bajas_registradas}</dd>
              </div>
              <div>
                <dt className="text-text-secondary">{t('evolucion.entregas_periodo')}</dt>
                <dd>
                  {final.kg_pienso !== null && inicial.kg_pienso !== null
                    ? `${formatoDecimal(restarDecimal(final.kg_pienso, inicial.kg_pienso))} kg`
                    : '—'}
                </dd>
              </div>
            </dl>
            <p className="mt-3 text-sm text-text-secondary">{t('evolucion.camion_aviso')}</p>
            <details className="mt-3 text-sm">
              <summary className="min-h-11 cursor-pointer font-semibold">
                {t('evolucion.contexto')}
              </summary>
              <p className="mb-3 text-text-secondary">{t('evolucion.tramo_aviso')}</p>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                {[inicial, final].map((d) => (
                  <section key={d.fecha} aria-label={fechaEvolucion(d.fecha)}>
                    <h3 className="mb-2 font-semibold">{fechaEvolucion(d.fecha)}</h3>
                    <dl className="space-y-2 tabular-nums">
                      {[
                        [t('evolucion.aves_restantes'), formatoDecimal(d.aves_restantes, 0)],
                        [t('liquidacion.bajas'), formatoDecimal(d.bajas_registradas, 0)],
                        [
                          t('evolucion.pienso_acumulado'),
                          d.kg_pienso === null ? '—' : `${formatoDecimal(d.kg_pienso)} kg`,
                        ],
                        [
                          t('evolucion.kg_confirmados'),
                          `${formatoDecimal(d.kg_salidas_confirmadas)} kg`,
                        ],
                        [
                          t('evolucion.kg_restantes'),
                          d.kg_salida_simulado === null
                            ? '—'
                            : `${formatoDecimal(restarDecimal(d.kg_salida_simulado, d.kg_salidas_confirmadas))} kg`,
                        ],
                        [
                          t('liquidacion.kg_salida'),
                          d.kg_salida_simulado === null
                            ? '—'
                            : `${formatoDecimal(d.kg_salida_simulado)} kg`,
                        ],
                        [
                          t('liquidacion.dias_engorde'),
                          formatoDecimal(d.resultado?.dias_engorde, 0),
                        ],
                        [t('liquidacion.edad_total'), formatoDecimal(d.resultado?.edad_total, 0)],
                        [
                          t('liquidacion.referencia'),
                          d.resultado
                            ? `${formatoDecimal(d.resultado.porcentaje_referencia)} %`
                            : '—',
                        ],
                      ].map(([label, value]) => (
                        <div key={label} className="flex justify-between gap-3">
                          <dt className="text-text-secondary">{label}</dt>
                          <dd className="break-all text-right">{value}</dd>
                        </div>
                      ))}
                      {d.resultado?.tramo_aplicado && (
                        <div className="flex justify-between gap-3">
                          <dt className="text-text-secondary">{t('evolucion.tramo')}</dt>
                          <dd>
                            {d.resultado.tramo_aplicado.edad_min}–
                            {d.resultado.tramo_aplicado.edad_max ?? '∞'} {t('liquidacion.dias')}
                          </dd>
                        </div>
                      )}
                    </dl>
                    <div className="mt-3">
                      <EvolucionAvisos dia={d} />
                    </div>
                  </section>
                ))}
              </div>
            </details>
          </>
        )}
      </section>
      <section className="mt-7 border-t border-border pt-4" aria-label={t('evolucion.grafica')}>
        <h2 className="mb-3 text-base font-semibold">{t('evolucion.grafica')}</h2>
        <div className="h-60 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={grafica} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
              <XAxis
                dataKey="fecha"
                tickFormatter={(f) =>
                  new Intl.DateTimeFormat('es-ES', { day: '2-digit', month: '2-digit' }).format(
                    new Date(`${f}T12:00:00`),
                  )
                }
                minTickGap={30}
                tick={{ fill: 'var(--c-text-secondary)', fontSize: 12 }}
              />
              <YAxis width={65} tick={{ fill: 'var(--c-text-secondary)', fontSize: 12 }} />
              <Tooltip
                content={({ active, payload }) =>
                  active && payload?.[0] ? (
                    <div className="rounded-lg border border-border bg-surface p-3 text-sm">
                      <p>{fechaEvolucion(payload[0].payload.fecha)}</p>
                      <p className="font-semibold tabular-nums">
                        {formatoEuros(payload[0].payload.exacto)}
                      </p>
                    </div>
                  ) : null
                }
              />
              <Line
                type="linear"
                dataKey="importe"
                stroke="var(--c-accent-icon)"
                strokeWidth={2}
                dot={false}
                connectNulls={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <p className="text-sm text-text-secondary">{t('evolucion.grafica_ayuda')}</p>
      </section>
      <section className="mt-7 border-t border-border pt-4">
        <h2 className="mb-3 text-base font-semibold">
          {t(
            ventana && !ventana.variacion_total_eur.startsWith('-')
              ? 'evolucion.menor_incremento'
              : 'evolucion.mayor_caida',
          )}
        </h2>
        {ventana ? (
          <>
            <p className="text-sm">
              {fechaEvolucion(ventana.desde)} – {fechaEvolucion(ventana.hasta)}:{' '}
              <strong className="tabular-nums">{formatoEuros(ventana.variacion_total_eur)}</strong>
            </p>
            <p className="mt-2 text-sm text-text-secondary">
              {t('evolucion.ventana_eventos', {
                bajas: ventana.bajas_periodo,
                kg: formatoDecimal(ventana.kg_entregados_periodo),
              })}
            </p>
          </>
        ) : (
          <p className="text-sm text-text-secondary">{t('evolucion.sin_ventanas')}</p>
        )}
      </section>
      <details className="mt-7 border-t border-border pt-4 text-sm">
        <summary className="min-h-11 cursor-pointer font-semibold">
          {t('evolucion.fuentes')}
        </summary>
        <ul className="space-y-4">
          {final.pesos.map((p) => (
            <li key={p.lote_nave_id}>
              <p className="font-medium">
                {p.nave_codigo} · {formatoDecimal(p.aves_restantes, 0)} {t('evolucion.aves')}
              </p>
              <p className="text-text-secondary">
                {p.fecha_muestreo
                  ? t('evolucion.muestreo', {
                      fecha: fechaEvolucion(p.fecha_muestreo),
                      dias: p.antiguedad_dias,
                    })
                  : t('evolucion.sin_muestreo')}
              </p>
              <p>
                {t(`evolucion.origen.${p.origen}`)} · {formatoDecimal(p.peso_medio_kg, 3)} kg/
                {t('evolucion.ave')}
              </p>
              {p.camadas_referencia.length > 0 && (
                <p className="text-text-secondary">
                  {t('evolucion.referencias', { ids: p.camadas_referencia.join(', ') })}
                </p>
              )}
            </li>
          ))}
        </ul>
      </details>
      <Link
        to={`/liquidacion/${data.camada_id}`}
        className="mt-5 inline-flex min-h-11 items-center text-sm font-medium text-accent-icon"
      >
        {t('evolucion.formulario')}
      </Link>
    </>
  );
}
